// ─── Shared utilities ─────────────────────────────────────────────────────────
// Extracted from duplicated definitions across multiple pages.

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Merge Tailwind classes safely — shadcn/ui standard utility. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

/**
 * Avatars are monochrome — see `.admin-avatar`.
 *
 * The colour hashing this replaced mapped a name onto eight hues. The DS ships
 * one accent plus three chart series and no identity palette, so those eight
 * slots were drawn from the status tokens: two pairs already collided, in the
 * light register accent-brand / status-info / chart-1 are the same value so a
 * third pair collapsed too, and one slot in eight rendered a person as
 * status-error. Initials on a raised surface carry the identity instead.
 */
export function getInitials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

// ─── API base URL ─────────────────────────────────────────────────────────────

// The `typeof window` guard is what keeps this module importable under node.
// It is evaluated at import time, so an unguarded read makes every node test
// that transitively imports anything from here die with "window is not
// defined" — including tests of pure functions that never touch a browser.
// Outside a browser it falls through to '', the same same-origin base a
// deployed (non-localhost) build already gets.
export const API_BASE = import.meta.env.VITE_CORE_API
  || import.meta.env.VITE_IDENTITY_API_URL
  || (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'http://localhost:3000' : '');

// ─── Public webhook base URL ─────────────────────────────────────────────────
// Stable prefix for webhook URLs we show a user to paste into a provider's
// dashboard. These outlive our infra: once registered with a provider they are
// never revisited, so they must not carry a backend hostname that we might
// rename or re-home. They point at the fastn edge (webhooks.fastn.dev), which
// proxies to whichever backend is current — see services/event/src/lib/webhook-url.ts
// for the backend-side twin, and keep the two in sync. Unset falls back to this
// environment's own API host (local dev, or an env with no edge in front).
export const WEBHOOK_BASE = import.meta.env.VITE_TRIGGER_WEBHOOK_BASE_URL
  || `${API_BASE}/api/v1/triggers`;

/** Build a public trigger webhook URL from its path segments. */
export function triggerWebhookUrl(...segments: string[]): string {
  return [WEBHOOK_BASE.replace(/\/+$/, ''), ...segments.map(encodeURIComponent)].join('/');
}

// ─── App base path ──────────────────────────────────────────────────────────
// The SPA is normally served at the domain root ("/"), but on the live.fastn.ai
// CloudFront edge it is mounted under "/gcp/". Vite injects the build-time `base`
// as import.meta.env.BASE_URL ("/" or "/gcp/"). BrowserRouter uses BASE_PATH as
// its basename; any redirect that bypasses the router (raw window.location or a
// Keycloak redirectUri) must go through APP_BASE / appUrl() so it lands inside
// the SPA mount instead of the domain root — which on live.fastn.ai is a
// different application.
export const APP_BASE = import.meta.env.BASE_URL;      // "/" or "/gcp/"
export const BASE_PATH = APP_BASE.replace(/\/+$/, ''); // ""  or "/gcp"

/**
 * Absolute URL (origin + app base + path) for external redirect URIs.
 * Built on APP_BASE so the app root keeps its trailing slash ("…/gcp/"): on the
 * live.fastn.ai edge the CloudFront "/gcp/*" behavior does not match a bare "/gcp",
 * so a slashless home URL would fall through to the other app at the domain root.
 */
export function appUrl(path = ''): string {
  return `${window.location.origin}${APP_BASE}${path.replace(/^\/+/, '')}`;
}

// ─── Date formatting ──────────────────────────────────────────────────────────

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(date: string | Date): string {
  return new Date(date).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export function timeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ─── Number formatting ────────────────────────────────────────────────────────

export function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return '—';
  if (bytes >= 1024 ** 4) return `${(bytes / 1024 ** 4).toFixed(1)} TB`;
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

export function formatLimit(value: number, unit?: string): string {
  if (value === -1) return 'Unlimited';
  return formatNumber(value) + (unit ? ` ${unit}` : '');
}

export function pctOf(current: number, limit: number): number {
  if (limit <= 0) return 0;
  return Math.min(100, Math.round((current / limit) * 100));
}

// ─── Slug ─────────────────────────────────────────────────────────────────────

export function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

// ─── OAuth Proxy URL ─────────────────────────────────────────────────────────
// getOAuthProxyUrl / resolveOAuthRedirectUrl live in lib/oauth-redirect.ts —
// that module reads window.location at import time, which breaks node tests.

// ─── Duplicate-name numbering ─────────────────────────────────────────────────

// Pairs each item with a display label. When the same base name occurs more than
// once in the list, its occurrences are numbered (" 1", " 2", …) so duplicates
// are distinguishable; a name that appears only once is left untouched.
export function withDuplicateNumbers<T>(
  items: T[],
  getName: (item: T) => string,
): Array<{ item: T; label: string; name: string; num: number }> {
  const totals: Record<string, number> = {};
  for (const it of items) {
    const n = getName(it);
    totals[n] = (totals[n] ?? 0) + 1;
  }
  const seen: Record<string, number> = {};
  return items.map((it) => {
    const n = getName(it);
    // num > 0 only when the name is duplicated. `label` is the combined form
    // ("name N"); `name` + `num` are exposed separately so callers with a
    // truncating layout can keep the number visible past the ellipsis.
    if ((totals[n] ?? 0) > 1) {
      seen[n] = (seen[n] ?? 0) + 1;
      return { item: it, label: `${n} ${seen[n]}`, name: n, num: seen[n] };
    }
    return { item: it, label: n, name: n, num: 0 };
  });
}

// Splits a label into its base and a trailing "default"/number token if present
// ("Widget installation 2" → { base: "Widget installation", token: "2" };
// "Widget installation default" → { …, token: "default" }). Lets a truncating row
// keep the token visible in its own slot past the ellipsis.
export function splitTrailingToken(label: string): { base: string; token: string | null } {
  const m = label.match(/^(.*\S)\s+(default|\d+)$/i);
  if (m) return { base: m[1], token: m[2] };
  return { base: label, token: null };
}

// ─── Files ────────────────────────────────────────────────────────────────────

/**
 * Basename of a path, for a download attribute.
 *
 * Lives here rather than beside its one caller (the orchestrator present
 * drawer) so it can be unit-tested: that component's import graph reaches
 * posthog-js, which throws at module eval outside a browser.
 */
export function docFilename(path: string): string {
  const base = path.split("/").pop() ?? "";
  return base || "document.md";
}
